import { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Home,
  BookOpen,
  Trophy,
  BarChart3,
  Settings,
  X,
  ChevronLeft,
  ChevronRight,
  Users2,
  Building,
  Globe,
  Compass,
  ClipboardList,
  GitBranch,
  Map,
  FolderKanban,
  FolderOpen,
  Network,
  User,
  LogOut,
  ChevronUp,
  Flame,
  Beaker,
  Shield,
} from 'lucide-react';
import { getAppNavigation } from '@/data/mockNavigation';
import { TutaraAvatar } from '../tutara-ui';
import { getStreakInfo } from '@/services/progress/dashboard';
import { useAuthStore } from '@/stores/auth-store';
import { useEnterprisePlan } from '@/hooks/useEnterprisePlan';
import { isAdminRole } from '@/utils/roles';
import { adminUrl } from '@/config/portals';

interface AppSidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
  user: {
    name: string;
    email: string;
    avatar?: string | null;
  };
  onLogout: () => void;
}

// Icon mapping by label
const iconMap: Record<string, typeof Home> = {
  'Dashboard': Home,
  'My Learning': BookOpen,
  'Explore': Compass,
  'Study Plans': ClipboardList,
  'My Skills': GitBranch,
  'Career Roadmap': Map,
  'Achievements': Trophy,
  'Progress': BarChart3,
  'Programs': FolderKanban,
  'Content Library': FolderOpen,
  'Skills Taxonomy': Network,
  'Employees': Users2,
  'Reports': BarChart3,
  'Tenant Settings': Settings,
  'Domains': Globe,
  'Organizations': Building,
  'Concept Lab': Beaker,
};

// Human-readable label for the subscription plan shown in the user menu.
// `plan` comes from useEnterprisePlan (org.subscriptionPlan, backend casing
// varies), so normalize case and tolerate the 'team' alias for PRO.
function getPlanLabel(plan: string | undefined): string {
  switch (plan?.toUpperCase()) {
    case 'ENTERPRISE':
      return 'Enterprise Plan';
    case 'PRO':
    case 'TEAM':
      return 'Pro Plan';
    default:
      return 'Free Plan';
  }
}

// Section headers configuration
const sectionHeaders: Record<string, string> = {
  'learning': 'My Learning',
  'development': 'My Development',
  'progress': 'Progress',
  'enterprise': 'Enterprise',
  'tenant': 'Tenant Management',
};

/**
 * Learner sidebar. Renders the everyday learner navigation only — the admin
 * console lives in its own shell (AdminLayout + AdminSidebar) as of Phase 2, so
 * there are no admin/enterprise branches or view-mode toggle here. An admin who
 * is browsing the learner app gets a single "Admin Console" link to cross into
 * the admin shell; which shell you see is URL-driven, not a stored flag.
 */
export default function AppSidebar({ isOpen, isCollapsed, onClose, onToggleCollapse, user, onLogout }: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAuthReady, user: authUser, currentOrganization } = useAuthStore();
  const isAdmin = isAdminRole(authUser?.roles);
  const { plan } = useEnterprisePlan();
  // Members of an org see the org's identity (org name); individuals without an
  // org see their personal subscription tier. "Plan" is a billing concept that
  // isn't actionable for regular org members, so we don't surface it to them.
  const accountLabel = currentOrganization?.name ?? getPlanLabel(plan);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [streak, setStreak] = useState(0);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Fetch streak from the same API used by the dashboard — only when auth is validated
  useEffect(() => {
    if (!isAuthReady || !isAuthenticated) return;
    getStreakInfo()
      .then(data => setStreak(data.currentStreak))
      .catch(() => { /* falls back to 0 */ });
  }, [isAuthenticated]);

  // Click outside handler for user menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Get navigation data from mock API and map icons
  const navigationData = getAppNavigation();
  const navigationItems = navigationData.map(item => {
    const icon = iconMap[item.label] || Home;
    return {
      ...item,
      icon,
      href: item.path || item.href || '/'
    };
  });

  // Group navigation items by section
  const groupedNavigation = navigationItems.reduce((acc, item) => {
    const section = item.section || 'main';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(item);
    return acc;
  }, {} as Record<string, typeof navigationItems>);

  const NavItem = ({ item }: { item: typeof navigationItems[0] }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.href;

    return (
      <NavLink
        to={item.href}
        title={isCollapsed ? item.label : undefined}
        className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? 'bg-tutara-primary text-white'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg'
        }`}
        onClick={() => onClose()}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!isCollapsed && <span className="font-medium">{item.label}</span>}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-30 h-screen bg-white dark:bg-tutara-dark-mode-surface border-r border-gray-200 dark:border-tutara-dark-mode-border transform transition-all duration-300 lg:translate-x-0 lg:top-16 lg:h-[calc(100vh-4rem)] flex flex-col overflow-visible ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'w-16' : 'w-64'}`}
      >
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200 dark:border-tutara-dark-mode-border">
          <div className="flex items-center">
            <img
              src="/logos/icon.png"
              alt="Tutara"
              className="h-8 w-8"
            />
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Desktop Collapse Toggle */}
        <div className="hidden lg:flex items-center justify-end p-2 border-b border-gray-200 dark:border-tutara-dark-mode-border">
          <button
            onClick={onToggleCollapse}
            className="p-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`p-4 space-y-2 overflow-y-auto flex-1 ${isCollapsed ? 'px-2' : ''}`}>
          {/* Admin Console — entry into the dedicated admin shell. Admins only;
              learners never see any admin chrome. In production the console is
              served from the `admin.` subdomain, so this crosses origins (an
              <a>, not a <NavLink>); on localhost it stays a same-origin path. */}
          {isAdmin && (() => {
            const target = adminUrl('/admin');
            const className = `flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} mb-4 px-4 py-3 rounded-lg border border-tutara-primary/30 text-tutara-primary hover:bg-tutara-primary/10 transition-colors`;
            return target.external ? (
              <a
                href={target.href}
                title={isCollapsed ? 'Admin Console' : undefined}
                data-testid="sidebar-admin-console-link"
                className={className}
                onClick={() => onClose()}
              >
                <Shield className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span className="font-medium">Admin Console</span>}
              </a>
            ) : (
              <NavLink
                to={target.href}
                title={isCollapsed ? 'Admin Console' : undefined}
                data-testid="sidebar-admin-console-link"
                className={className}
                onClick={() => onClose()}
              >
                <Shield className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span className="font-medium">Admin Console</span>}
              </NavLink>
            );
          })()}

          {/* Quick Stats */}
          {!isCollapsed && (
            <div className="mb-6 p-4 bg-gradient-to-r from-tutara-primary to-tutara-secondary rounded-lg text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Learning Streak</span>
                <span className="text-2xl font-bold">{streak} <Flame className="inline h-5 w-5" /></span>
              </div>
              <div className="text-xs opacity-90">
                {streak > 0 ? 'Keep it up! You\'re doing great.' : 'Start learning to build your streak!'}
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="mb-6 p-2 bg-gradient-to-r from-tutara-primary to-tutara-secondary rounded-lg text-white flex items-center justify-center" title={`${streak} day streak`}>
              <Flame className="h-5 w-5" />
            </div>
          )}

          {/* Dashboard - Main Section */}
          {groupedNavigation.main && (
            <div className="space-y-1">
              {groupedNavigation.main.map((item) => (
                <NavItem key={item.href} item={item} />
              ))}
            </div>
          )}

          {/* My Learning Section */}
          {groupedNavigation.learning && (
            <>
              {!isCollapsed && (
                <div className="mt-4 mb-2 px-4">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {sectionHeaders.learning}
                  </span>
                </div>
              )}
              {isCollapsed && <div className="my-3 border-t border-gray-200 dark:border-tutara-dark-mode-border" />}
              <div className="space-y-1">
                {groupedNavigation.learning.map((item) => (
                  <NavItem key={item.href} item={item} />
                ))}
              </div>
            </>
          )}

          {/* My Development Section */}
          {groupedNavigation.development && (
            <>
              {!isCollapsed && (
                <div className="mt-4 mb-2 px-4">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {sectionHeaders.development}
                  </span>
                </div>
              )}
              {isCollapsed && <div className="my-3 border-t border-gray-200 dark:border-tutara-dark-mode-border" />}
              <div className="space-y-1">
                {groupedNavigation.development.map((item) => (
                  <NavItem key={item.href} item={item} />
                ))}
              </div>
            </>
          )}

          {/* Progress Section */}
          {groupedNavigation.progress && (
            <>
              {!isCollapsed && (
                <div className="mt-4 mb-2 px-4">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {sectionHeaders.progress}
                  </span>
                </div>
              )}
              {isCollapsed && <div className="my-3 border-t border-gray-200 dark:border-tutara-dark-mode-border" />}
              <div className="space-y-1">
                {groupedNavigation.progress.map((item) => (
                  <NavItem key={item.href} item={item} />
                ))}
              </div>
            </>
          )}

          {/* Pro Upgrade Card */}
          {!isCollapsed && (
            <div className="mt-6 p-4 bg-gradient-to-br from-tutara-turquoise to-tutara-teal rounded-lg text-white">
              <h3 className="font-semibold mb-2">Upgrade to Pro</h3>
              <p className="text-xs mb-3 opacity-90">
                Unlock unlimited courses and advanced features
              </p>
              <button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="w-full py-2 bg-white/20 backdrop-blur-sm rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
              >
                Learn More
              </button>
            </div>
          )}
          {isCollapsed && (
            <button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="mt-6 p-2 bg-gradient-to-br from-tutara-turquoise to-tutara-teal rounded-lg text-white flex items-center justify-center"
              title="Upgrade to Pro"
            >
              <Trophy className="h-5 w-5" />
            </button>
          )}
        </nav>

        {/* User Profile Section - Fixed at bottom */}
        <div
          ref={userMenuRef}
          className={`relative border-t border-gray-200 dark:border-tutara-dark-mode-border bg-white dark:bg-tutara-dark-mode-surface flex-shrink-0 ${isCollapsed ? 'p-2' : 'p-3'}`}
        >
          {/* User Menu Dropdown */}
          {userMenuOpen && (
            <div className={`absolute bg-white dark:bg-tutara-dark-mode-surface rounded-lg shadow-xl border border-gray-200 dark:border-tutara-dark-mode-border z-50 ${isCollapsed ? 'left-full bottom-0 ml-2 w-48' : 'bottom-full left-0 right-0 mb-2 mx-2'}`}>
              <div className="p-2">
                {/* Switch to Admin — the return trip for the admin console's
                    "Switch to Personal". The nav link at the top of the sidebar
                    is the primary entry; mirroring it here matters because the
                    account menu is where a mode switch is looked for, and it
                    stays labelled when the sidebar is collapsed. */}
                {isAdmin && (() => {
                  const target = adminUrl('/admin');
                  const closeAll = () => {
                    setUserMenuOpen(false);
                    onClose();
                  };
                  const className = 'flex items-center space-x-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg';
                  const body = (
                    <>
                      <Shield className="h-5 w-5 text-tutara-primary" />
                      <span className="text-sm text-tutara-charcoal dark:text-white">Switch to Admin</span>
                    </>
                  );
                  return (
                    <>
                      {target.external ? (
                        <a
                          href={target.href}
                          data-testid="account-menu-switch-admin-link"
                          className={className}
                          onClick={closeAll}
                        >
                          {body}
                        </a>
                      ) : (
                        <Link
                          to={target.href}
                          data-testid="account-menu-switch-admin-link"
                          className={className}
                          onClick={closeAll}
                        >
                          {body}
                        </Link>
                      )}
                      <hr className="my-2 border-gray-200 dark:border-tutara-dark-mode-border" />
                    </>
                  );
                })()}
                <Link
                  to="/profile"
                  onClick={() => {
                    setUserMenuOpen(false);
                    onClose();
                  }}
                  className="flex items-center space-x-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg"
                >
                  <User className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-tutara-charcoal dark:text-white">Profile</span>
                </Link>
                <hr className="my-2 border-gray-200 dark:border-tutara-dark-mode-border" />
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    onLogout();
                  }}
                  className="flex items-center space-x-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg w-full text-left"
                >
                  <LogOut className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-tutara-charcoal dark:text-white">Logout</span>
                </button>
              </div>
            </div>
          )}

          {/* User Button */}
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} p-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg transition-colors`}
            title={isCollapsed ? user.name : undefined}
          >
            <TutaraAvatar
              src={user.avatar || undefined}
              alt={user.name}
              fallback={user.name.split(' ').map(n => n[0]).join('')}
              size="sm"
            />
            {!isCollapsed && (
              <>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-tutara-charcoal dark:text-white truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-tutara-primary dark:text-tutara-primary/80 truncate font-medium">
                    {accountLabel}
                  </p>
                </div>
                <ChevronUp className={`h-4 w-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
